import { appShell as defaultAppShell } from '../../../theme/tokens/semantic';
import { shadows as defaultShadows } from '../../../theme/tokens/shadows';
import { APP_SHELL_Z_INDEX } from '../constants/appShell.constants';
import { createSidebarTypography } from './sidebarTypography';

export function createAppShellSx(tokenOverrides = {}) {
  const appShell = tokenOverrides.appShell ?? defaultAppShell;
  const shadows = tokenOverrides.shadows ?? defaultShadows;
  const { sidebar, header, shell } = appShell;
  const sidebarType = createSidebarTypography(sidebar);

  return {
    layoutShell: {
      display: 'flex',
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      width: '100%',
      overflow: 'hidden',
      bgcolor: shell.background,
    },
    mainColumn: {
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative',
      zIndex: 1,
    },
    mainColumnNoHeader: {
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    contentRegion: {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      bgcolor: shell.background,
    },
    contentRegionScroll: {
      overflowY: 'auto',
    },
    contentRegionLocked: {
      overflowY: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      // Ensure the locked route shell fills the column under the header.
      height: '100%',
      '& > *': {
        flex: 1,
        minHeight: 0,
        minWidth: 0,
      },
    },
    mobileMenuButton: {
      display: { xs: 'inline-flex', md: 'none' },
      position: 'fixed',
      top: 9,
      left: 12,
      zIndex: APP_SHELL_Z_INDEX.mobileMenuButton,
      bgcolor: shell.mobileMenuButtonBg,
      boxShadow: shadows.sm,
    },
    mobileMenuButtonNoHeader: {
      top: 16,
    },
    drawerPaper: (width) => ({
      width,
      boxSizing: 'border-box',
      borderRight: `1px solid ${sidebar.border}`,
      overflowX: 'hidden',
      overflowY: 'hidden',
      boxShadow: 'none',
    }),
    sidebarWrapper: {
      position: 'relative',
      flexShrink: 0,
      zIndex: APP_SHELL_Z_INDEX.sidebarWrapper,
    },
    sidebarCollapseToggle: {
      position: 'absolute',
      top: 28,
      right: -12,
      zIndex: APP_SHELL_Z_INDEX.sidebarToggle,
      width: 24,
      height: 24,
      p: 0,
      bgcolor: sidebar.toggleBg,
      border: `1px solid ${sidebar.toggleBorder}`,
      color: sidebar.toggleIcon,
      boxShadow: shadows.sm,
      borderRadius: '50%',
    },
    sidebarCollapseToggleIcon: {
      fontSize: 14,
    },
    drawerTemporaryPaper: {
      width: 260,
      boxSizing: 'border-box',
      border: 'none',
      boxShadow: shadows.xl,
    },
    sidebarRoot: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: sidebar.bg,
      color: sidebar.text,
      position: 'relative',
      ...sidebarType.root,
    },
    sidebarScroll: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      py: 1.5,
      px: 0,
      '&::-webkit-scrollbar': { width: 5 },
      '&::-webkit-scrollbar-thumb': {
        bgcolor: sidebar.scrollbarThumb,
        borderRadius: 999,
      },
      '&::-webkit-scrollbar-track': {
        bgcolor: 'transparent',
      },
    },
    sidebar,
    sidebarType,
    header: {
      appBar: {
        position: { sm: 'sticky', xs: 'fixed' },
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        bgcolor: header.background,
        color: header.text,
        width: '100%',
        flexShrink: 0,
        boxShadow: 'none',
        borderBottom: `1px solid ${header.border}`,
      },
      appBarTest: {
        position: 'sticky',
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        bgcolor: header.background,
        color: header.text,
        width: '100%',
        flexShrink: 0,
        boxShadow: 'none',
        borderBottom: `1px solid ${header.border}`,
      },
      toolbar: {
        justifyContent: 'space-between',
        px: 0,
        minHeight: { xs: 56, sm: 64 },
      },
      toolbarTest: {
        justifyContent: 'space-between',
        px: 0,
        flexWrap: 'wrap',
        minHeight: { xs: 56, sm: 64 },
      },
      brandTitle: {
        fontWeight: 700,
        color: header.text,
        fontSize: { xs: '12px', sm: '14px' },
      },
      brandSubtitle: {
        fontStyle: 'italic',
        color: header.textMuted,
        display: { xs: 'none', sm: 'block' },
      },
      brandTitleTest: {
        fontWeight: 600,
        color: header.text,
        fontSize: { xs: '12px', sm: '14px' },
      },
      brandSubtitleTest: {
        fontStyle: 'italic',
        color: header.textMuted,
        fontSize: { xs: '11px', sm: '13px' },
      },
      divider: {
        mx: 2,
        height: 'auto',
        display: { xs: 'none', sm: 'block' },
        borderColor: header.border,
      },
      logoBox: {
        width: { xs: '100px', sm: '100px' },
        height: { xs: '50px', sm: '50px' },
        ml: { xs: 1.3, sm: 0 }
      },
      logoBoxTest: {
        width: '110px',
        height: '45px',
        ml: { xs: 2.5, sm: 0 }
      },
    },
  };
}
